import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
} from "react";
import { apiService } from "../services/apiService";

// Initial state
const initialState = {
  isLoading: false,
  error: null,
  resumeData: null,
  isSubmitting: false,
  submitSuccess: false,
  lastDate: null,
  isLoadingLastDate: false,
  lastDateError: null,
  resumeId: null,
  jobDescriptionId: null,
};

// Action types
const actionTypes = {
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  SET_RESUME_DATA: "SET_RESUME_DATA",
  SET_SUBMITTING: "SET_SUBMITTING",
  SET_SUBMIT_SUCCESS: "SET_SUBMIT_SUCCESS",
  SET_LAST_DATE: "SET_LAST_DATE",
  SET_LOADING_LAST_DATE: "SET_LOADING_LAST_DATE",
  SET_LAST_DATE_ERROR: "SET_LAST_DATE_ERROR",
  RESET_STATE: "RESET_STATE",
  SET_IDS: "SET_IDS",
  SET_INTERVIEW_ID: "SET_INTERVIEW_ID",
};

// Reducer
const welcomeReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.SET_IDS:
      return {
        ...state,
        resumeId: action.payload.resumeId,
        jobDescriptionId: action.payload.jobDescriptionId,
      };
    case actionTypes.SET_INTERVIEW_ID:
      return {
        ...state,
        interviewId: action.payload,
      };

    case actionTypes.SET_LOADING:
      return { ...state, isLoading: action.payload };
    case actionTypes.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };
    case actionTypes.SET_RESUME_DATA:
      return { ...state, resumeData: action.payload };
    case actionTypes.SET_SUBMITTING:
      return { ...state, isSubmitting: action.payload };
    case actionTypes.SET_SUBMIT_SUCCESS:
      return { ...state, submitSuccess: action.payload, isSubmitting: false };
    case actionTypes.SET_LAST_DATE:
      return { ...state, lastDate: action.payload, isLoadingLastDate: false };
    case actionTypes.SET_LOADING_LAST_DATE:
      return { ...state, isLoadingLastDate: action.payload };
    case actionTypes.SET_LAST_DATE_ERROR:
      return {
        ...state,
        lastDateError: action.payload,
        isLoadingLastDate: false,
      };
    case actionTypes.RESET_STATE:
      return initialState;
    default:
      return state;
  }
};

// Create context
const WelcomeContext = createContext();

// Context provider
export const WelcomeProvider = ({ children }) => {
  const [state, dispatch] = useReducer(welcomeReducer, initialState);

  const setInterviewId = useCallback((interviewId) => {
    dispatch({
      type: actionTypes.SET_INTERVIEW_ID,
      payload: interviewId,
    });
  }, []);

  const setIds = useCallback((resumeId, jobDescriptionId) => {
    dispatch({
      type: actionTypes.SET_IDS,
      payload: { resumeId, jobDescriptionId },
    });
  }, []);

  // Helper functions
  const setLoading = useCallback((loading) => {
    dispatch({ type: actionTypes.SET_LOADING, payload: loading });
  }, []);

  const setError = useCallback((error) => {
    dispatch({ type: actionTypes.SET_ERROR, payload: error });
  }, []);

  const setSubmitting = useCallback((submitting) => {
    dispatch({ type: actionTypes.SET_SUBMITTING, payload: submitting });
  }, []);

  const setSubmitSuccess = useCallback((success) => {
    dispatch({ type: actionTypes.SET_SUBMIT_SUCCESS, payload: success });
  }, []);

  const resetState = useCallback(() => {
    dispatch({ type: actionTypes.RESET_STATE });
  }, []);

  // Fetch interview last date - memoized to prevent unnecessary re-renders
  const fetchInterviewLastDate = useCallback(async (jobDescriptionId) => {


    if (!jobDescriptionId) {
      console.warn("No jobDescriptionId provided to fetchInterviewLastDate");
      return { success: false, error: "No job description ID provided" };
    }

    dispatch({ type: actionTypes.SET_LOADING_LAST_DATE, payload: true });
    dispatch({ type: actionTypes.SET_LAST_DATE_ERROR, payload: null });

    try {

      const response = await apiService.getInterviewLastDate(jobDescriptionId);


      dispatch({ type: actionTypes.SET_LAST_DATE, payload: response });
      return { success: true, data: response };
    } catch (error) {
      console.error("Error fetching interview last date:", error);
      dispatch({
        type: actionTypes.SET_LAST_DATE_ERROR,
        payload: error.message,
      });
      return { success: false, error: error.message };
    }
  }, []);

  // Transform form data to API format
  const transformFormData = useCallback((formData) => {
    // Parse currency code to extract currency
    const currencyMatch = formData.currencyCode?.match(/([A-Z]{3})/);
    const currency = currencyMatch ? currencyMatch[1] : "USD";

    return {
      ctc_currency: currency,
      current_ctc: parseInt(formData.currentCTC) || 0,
      expected_ctc: parseInt(formData.expectedCTC) || 0,
      phone_number: formData.phoneNumber || "",
      gender: formData.gender || "",
      age: parseInt(formData.age) || 0,
      ethnicity: formData.ethnicity || "",
      language_proficiency: formData.language || [],
      total_experience: parseInt(formData.experience) || 0,
      wfh: formData.isWFH || false,
    };
  }, []);

  // Main submit function
  const submitWelcomeForm = useCallback(
    async (formData, resumeId, jobDescriptionId) => {
      setSubmitting(true);
      setError(null);

      try {
        // Step 1: Update resume with user details
        const apiData = transformFormData(formData);
        await apiService.updateResume(resumeId, apiData);

        // Step 2: Update resume locations (only if locations exist)
        if (formData.location && formData.location.length > 0) {
          await apiService.updateResumeLocation(resumeId, formData.location);
        }

        // Step 3: Send interview email
        await apiService.sendInterviewEmail(resumeId, jobDescriptionId);

        setSubmitSuccess(true);
        return { success: true };
      } catch (error) {
        setError(error.message);
        return { success: false, error: error.message };
      } finally {
        setSubmitting(false);
      }
    },
    [setSubmitting, setError, setSubmitSuccess, transformFormData]
  );

  const value = {
    ...state,
    setInterviewId,
    setIds,
    submitWelcomeForm,
    fetchInterviewLastDate,
    setError,
    resetState,
  };

  return (
    <WelcomeContext.Provider value={value}>{children}</WelcomeContext.Provider>
  );
};

// Custom hook to use the context
export const useWelcome = () => {
  const context = useContext(WelcomeContext);
  if (!context) {
    throw new Error("useWelcome must be used within a WelcomeProvider");
  }
  return context;
};
